import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, ArrowLeft, Loader2, Code } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import ImageUpload from '../../components/common/ImageUpload';
import RichTextEditor from '../../components/common/RichTextEditor';
import { useAuth } from '../../context/AuthContext';
import useBlogs from '../../hooks/useBlogs';
import blogService from '../../services/blogService';

const isRichTextEmpty = (value) => {
  if (!value) return true;

  const plainText = value
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .trim();

  return plainText === '';
};

const BlogForm = () => {
  const { hasPermission } = useAuth();
  const canChangeStatus = hasPermission('blogs.change_status');
  const { id } = useParams();
  const navigate = useNavigate();
  const { addBlog, updateBlog } = useBlogs();
  const [isLoading, setIsLoading] = useState(!!id);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editorMode, setEditorMode] = useState('visual');

  const [form, setForm] = useState({
    // Unified fields
    title: '',
    image: '',
    cardShortDescription: '',
    slug: '',
    longDescription: '',
    // Section 3: SEO
    metaTitle: '',
    metaDescription: '',
    canonicalUrl: '',
    seoImage: '',
    seoImageWidth: '',
    seoImageHeight: '',
    seoAuthor: '',
    twitterCreator: '',
    status: 'active',
    customScript: '',
  });

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      try {
        const data = await blogService.getBlogById(id);
        if (!data) return navigate('/blogs');
            setForm({
              title: data.card_title || data.details_title || data.title || '',
              image: data.card_image_url || data.details_image_url || data.image_url || '',
              cardShortDescription: data.card_short_description || data.short_description || '',
              slug: data.slug || '',
              longDescription: data.long_description || '',
              metaTitle: data.meta_title || '',
              metaDescription: data.meta_description || '',
              canonicalUrl: data.canonical_url || '',
              seoImage: data.seo_image_url || '',
              seoImageWidth: data.seo_image_width || '',
              seoImageHeight: data.seo_image_height || '',
              seoAuthor: data.seo_author || '',
              twitterCreator: data.twitter_creator || '',
              status: data.status || 'active',
              customScript: data.custom_script || '',
            });
      } catch (error) {
        navigate('/blogs');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [id, navigate]);

  const handleChange = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const generateSlug = (text) => {
    return text.toString().toLowerCase().trim().replace(/[^a-z0-9 -]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-');
  };

  useEffect(() => {
    if (!id) {
      // auto-generate slug from title when creating
      if (form.title && (!form.slug || form.slug.trim() === '')) {
        handleChange('slug', generateSlug(form.title));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.title]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // validation
    if (!form.title || form.title.trim() === '') return toast.error('Blog Title is required');
    if (!form.image || form.image.trim() === '') return toast.error('Blog Image is required');
    if (!form.cardShortDescription || form.cardShortDescription.trim() === '') return toast.error('Blog Card Short Description is required');
    if (isRichTextEmpty(form.longDescription)) return toast.error('Blog Long Description is required');

    setIsSubmitting(true);
    const payload = {
      // Use single title/image for both card and details
      cardTitle: form.title,
      detailsTitle: form.title,
      cardImage: form.image,
      detailsImage: form.image,
      cardShortDescription: form.cardShortDescription,
      // Slug
      slug: form.slug,
      longDescription: form.longDescription,
      // SEO
      metaTitle: form.metaTitle,
      metaDescription: form.metaDescription,
      canonicalUrl: form.canonicalUrl,
      seoImage: form.seoImage,
      seoImageWidth: form.seoImageWidth ? Number(form.seoImageWidth) : null,
      seoImageHeight: form.seoImageHeight ? Number(form.seoImageHeight) : null,
      seoAuthor: form.seoAuthor,
      twitterCreator: form.twitterCreator,
      status: form.status,
      customScript: form.customScript,
    };

    try {
      let success = false;
      if (id) {
        success = await updateBlog(id, payload);
      } else {
        success = await addBlog(payload);
      }
      if (success) navigate('/blogs');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <Loader2 className="animate-spin text-primary mb-4" size={40} />
        <p className="text-slate-500 font-medium">Loading Blog Editor...</p>
      </div>
    );
  }

  return (
    <div className="pb-20 bg-slate-50 min-h-screen">
      {/* Sticky Header */}
      <div className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-200 shadow-sm px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/blogs')}
            className="rounded-full h-10 w-10 p-0 hover:bg-slate-100"
          >
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">{id ? 'Edit Blog' : 'Create Blog'}</h1>
            <p className="text-slate-500">Add or edit blog posts</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate('/blogs')} disabled={isSubmitting} className="rounded-full px-6">Cancel</Button>
          <Button onClick={handleSubmit} disabled={isSubmitting} className="bg-primary text-secondary rounded-full px-8 gap-2">
            {isSubmitting ? (
              <Loader2 className="animate-spin" size={18} />
            ) : (
              <Save size={18} />
            )}
            {id ? 'Save Changes' : 'Create Blog'}
          </Button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="max-w-4xl mx-auto mt-8 space-y-6 px-6">
        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm space-y-4">
          <div>
            <Label>Blog Title <span className="text-red-500">*</span></Label>
            <Input value={form.title} onChange={e => handleChange('title', e.target.value)} />
          </div>

          <div>
            <Label>Blog Image <span className="text-red-500">*</span></Label>
            <ImageUpload value={form.image} onChange={val => handleChange('image', val)} />
          </div>

          <div>
            <Label>Blog Card Short Description <span className="text-red-500">*</span></Label>
            <textarea value={form.cardShortDescription} onChange={e => handleChange('cardShortDescription', e.target.value)} className="w-full p-3 rounded-md border border-slate-200 min-h-[100px]" />
          </div>

          <div>
            <Label>Slug URL <span className="text-red-500">*</span></Label>
            <Input value={form.slug} onChange={e => handleChange('slug', e.target.value)} />
          </div>

          {/* Details title/image unified with Blog Title and Blog Image */}

          <div>
            <div className="flex items-center justify-between gap-4 mb-2">
              <Label>Blog Long Description <span className="text-red-500">*</span></Label>
              <div className="inline-flex rounded-md border border-slate-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setEditorMode('visual')}
                  className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                    editorMode === 'visual'
                      ? 'bg-slate-900 text-white'
                      : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  Visual Editor
                </button>
                <button
                  type="button"
                  onClick={() => setEditorMode('html')}
                  className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                    editorMode === 'html'
                      ? 'bg-slate-900 text-white'
                      : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  HTML Paste
                </button>
              </div>
            </div>

            {editorMode === 'visual' ? (
              <RichTextEditor
                value={form.longDescription}
                onChange={val => handleChange('longDescription', val)}
                placeholder="Write the full blog content here..."
              />
            ) : (
              <div className="space-y-2">
                <textarea
                  value={form.longDescription}
                  onChange={e => handleChange('longDescription', e.target.value)}
                  placeholder="Paste full HTML content here, for example: <figure><img ... /></figure><p>Your content...</p>"
                  className="w-full min-h-[280px] rounded-md border border-slate-200 p-3 font-mono text-sm"
                />
                <p className="text-sm text-slate-500">
                  Paste your full blog HTML here. When you switch back to Visual Editor, the content will load into Quill.
                </p>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Meta Title</Label>
              <Input value={form.metaTitle} onChange={e => handleChange('metaTitle', e.target.value)} />
            </div>
            <div>
              <Label>Meta Description</Label>
              <Input value={form.metaDescription} onChange={e => handleChange('metaDescription', e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Canonical URL</Label>
              <Input value={form.canonicalUrl} onChange={e => handleChange('canonicalUrl', e.target.value)} placeholder="https://fiveonline.in/blogs/your-slug/" />
            </div>
            <div>
              <Label>SEO Image URL</Label>
              <Input value={form.seoImage} onChange={e => handleChange('seoImage', e.target.value)} placeholder="/figma/portfolio/blog-1.jpg or full URL" />
            </div>
            <div>
              <Label>SEO Image Width</Label>
              <Input value={form.seoImageWidth} onChange={e => handleChange('seoImageWidth', e.target.value)} placeholder="720" />
            </div>
            <div>
              <Label>SEO Image Height</Label>
              <Input value={form.seoImageHeight} onChange={e => handleChange('seoImageHeight', e.target.value)} placeholder="414" />
            </div>
            <div>
              <Label>SEO Author</Label>
              <Input value={form.seoAuthor} onChange={e => handleChange('seoAuthor', e.target.value)} placeholder="Five Online Web Solutions" />
            </div>
            <div>
              <Label>Twitter Creator</Label>
              <Input value={form.twitterCreator} onChange={e => handleChange('twitterCreator', e.target.value)} placeholder="@FiveOnline2" />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={value => handleChange('status', value)} disabled={!canChangeStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="deactive">Deactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="pt-2">
            <div className="flex items-center gap-2 mb-2">
              <Code className="text-primary" size={18} />
              <Label>Custom Scripts</Label>
            </div>
            <textarea
              value={form.customScript}
              onChange={e => handleChange('customScript', e.target.value)}
              placeholder='Optional SEO/schema script, for example: <script type="application/ld+json">{...}</script>'
              className="w-full min-h-[180px] rounded-md border border-slate-200 p-3 font-mono text-sm"
            />
            <p className="mt-2 text-xs text-slate-500">
              Optional. If added, this script will be injected only on this blog detail page.
            </p>
          </div>
        </div>
      </form>
    </div>
  );
};

export default BlogForm;
